import { useMemo, useState } from 'react';
import { EmptyState } from '../EmptyState';
import { SkeletonTable } from '../SkeletonTable';
import { exportToCsv } from '../../../utils/exportTable';
import { DataGridBody } from './DataGridBody';
import { DataGridHeader } from './DataGridHeader';
import { DataGridPagination } from './DataGridPagination';
import { DataGridToolbar } from './DataGridToolbar';
import type { DataGridProps, SortConfig } from './types';

function toTextValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}

export function DataGrid<T>({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  loading = false,
  error = null,
  selectable = false,
  selectedRows,
  onSelectionChange,
  getRowId,
  filters = [],
  activeFilters = [],
  onFilterChange,
  searchable = false,
  searchValue = '',
  onSearchChange,
  exportable = false,
  onExport,
  refreshable = false,
  onRefresh,
  autoRefreshOptions = [5, 10, 30, 60],
  autoRefreshInterval = 0,
  onAutoRefreshChange,
  emptyMessage = 'No records found.',
  toolbarActions,
  pageSizeOptions = [10, 25, 50, 100],
  onRowClick,
}: DataGridProps<T>) {
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string>>(new Set<string>());
  const selectedRowSet = selectedRows ?? internalSelectedRows;
  const resolveRowId = (row: T, index: number): string => getRowId?.(row) ?? String(index);

  const visibleColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns]);
  const rowIds = data.map((row, index) => resolveRowId(row, index));

  const emitSelection = (nextSelection: Set<string>) => {
    if (!selectedRows) setInternalSelectedRows(nextSelection);
    onSelectionChange?.(nextSelection);
  };

  const handleToggleSelectAll = (checked: boolean) => {
    const nextSelection = new Set(selectedRowSet);
    rowIds.forEach((rowId) => {
      if (checked) nextSelection.add(rowId);
      else nextSelection.delete(rowId);
    });
    emitSelection(nextSelection);
  };

  const handleToggleRow = (rowId: string, checked: boolean) => {
    const nextSelection = new Set(selectedRowSet);
    if (checked) nextSelection.add(rowId);
    else nextSelection.delete(rowId);
    emitSelection(nextSelection);
  };

  const handleSortChange = (nextSort: SortConfig) => {
    onSortChange?.(nextSort);
  };

  const handleDefaultExport = () => {
    if (onExport) {
      onExport();
      return;
    }

    exportToCsv(
      data,
      visibleColumns.map((column) => ({
        header: column.header,
        accessor: (row: T) =>
          toTextValue(typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor]),
      })),
      'data-grid-export',
    );
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      <DataGridToolbar
        totalCount={totalCount}
        searchable={searchable}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={onFilterChange}
        exportable={exportable}
        onExport={handleDefaultExport}
        refreshable={refreshable}
        onRefresh={onRefresh}
        autoRefreshOptions={autoRefreshOptions}
        autoRefreshInterval={autoRefreshInterval}
        onAutoRefreshChange={onAutoRefreshChange}
        toolbarActions={toolbarActions}
      />

      {error ? (
        <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-y border-red-200 dark:border-red-900/40" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <SkeletonTable rows={Math.max(3, Math.min(pageSize, 10))} columns={visibleColumns.length + (selectable ? 1 : 0)} />
      ) : data.length === 0 ? (
        <EmptyState title="No data" message={emptyMessage} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <DataGridHeader
              columns={columns}
              sort={sort}
              onSortChange={handleSortChange}
              selectable={selectable}
              rowIds={rowIds}
              selectedRows={selectedRowSet}
              onToggleSelectAll={handleToggleSelectAll}
            />
            <DataGridBody
              columns={columns}
              data={data}
              selectable={selectable}
              selectedRows={selectedRowSet}
              getRowId={resolveRowId}
              onToggleRow={handleToggleRow}
              onRowClick={onRowClick}
            />
          </table>
        </div>
      )}

      <DataGridPagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
    </div>
  );
}
